import {
  ArtifactTabActions,
  ArtifactTabItem,
  ArtifactTabList,
  ArtifactTabs,
} from '@heidi/design-system/artifact-tabs';
import { Button } from '@heidi/design-system/button';
import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import { Braces, LoaderCircle, Maximize2, Plus, Sparkles } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import type { ObjectWorkspaceWorkTabDescriptor } from './ObjectWorkspaceHarness';

interface ObjectWorkspaceHarnessHeaderProps {
  tabs: readonly ObjectWorkspaceWorkTabDescriptor[];
  activeTabId: string;
  objectName: string;
  getTabId: (tabId: string) => string;
  getPanelId: (tabId: string) => string;
  onTabSelect: (tabId: string) => void;
  onAddTab: () => void;
  onDismissTab?: (tabId: string) => void;
  /** Restores the hidden object pane. Omitted while the pane is showing. */
  onExpandObjectPane?: () => void;
  onOpenLastSession?: () => void;
  heidiTabIcon?: ReactNode;
}

export function ObjectWorkspaceHarnessHeader({
  tabs,
  activeTabId,
  objectName,
  getTabId,
  getPanelId,
  onTabSelect,
  onAddTab,
  onDismissTab,
  onExpandObjectPane,
  onOpenLastSession,
  heidiTabIcon,
}: ObjectWorkspaceHarnessHeaderProps): JSX.Element {
  const intl = useIntl();

  return (
    <header className="flex items-center gap-3 px-3 py-2">
      {onExpandObjectPane ? (
        <PanelToggleButton
          type="button"
          open={false}
          side="left"
          variant="ghost"
          size="sm"
          className="shrink-0 motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-left-1 motion-safe:rtl:slide-in-from-right-1"
          aria-label={intl.formatMessage(messages.expandObjectPane, {
            name: objectName,
          })}
          onClick={onExpandObjectPane}
        />
      ) : null}
      <ArtifactTabs
        value={activeTabId}
        onValueChange={onTabSelect}
        className="min-w-0 flex-1"
        data-testid="object-workspace-work-tabs"
      >
        <ArtifactTabList
          aria-label={intl.formatMessage(messages.workTabsLabel)}
          data-testid="object-workspace-work-tab-list"
        >
          {tabs.map((tab) => (
            <ArtifactTabItem
              key={tab.id}
              id={getTabId(tab.id)}
              value={tab.id}
              label={tab.title}
              icon={
                tab.kind === 'heidi' ? (
                  (heidiTabIcon ?? <Sparkles aria-hidden />)
                ) : tab.isThinking ? (
                  <LoaderCircle
                    aria-hidden
                    className="animate-spin motion-reduce:animate-none"
                  />
                ) : (
                  <Braces aria-hidden />
                )
              }
              className={[
                'h-7 data-[state=active]:shadow-md',
                tab.isDismissible ? 'pe-7 ps-2 [&>span]:![--af-tab-mask:none]' : 'px-2',
              ].join(' ')}
              aria-controls={getPanelId(tab.id)}
              data-testid={`object-workspace-work-tab-${tab.id}`}
              onDismiss={
                tab.isDismissible && onDismissTab ? () => onDismissTab(tab.id) : undefined
              }
              dismissLabel={intl.formatMessage(messages.dismissWorkTab, {
                title: tab.title,
              })}
            />
          ))}
        </ArtifactTabList>
        <ArtifactTabActions data-testid="object-workspace-work-tab-actions">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            className="shrink-0"
            aria-label={intl.formatMessage(messages.addWorkTab)}
            onClick={onAddTab}
          >
            <Plus aria-hidden />
          </Button>
        </ArtifactTabActions>
      </ArtifactTabs>
      {onOpenLastSession ? (
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onOpenLastSession}>
            {intl.formatMessage(messages.openLastSession)}
            <Maximize2 aria-hidden />
          </Button>
        </div>
      ) : null}
    </header>
  );
}
